import { z } from 'zod';
import type { GroupTag } from '../../types';
import type { WorkspaceFile, WorkspaceFolder, WorkspaceObject, WorkspaceFilePreviewData, WorkspaceFileSummaryData, WorkspaceFileTranscript } from '../../types/workspace';
import type { FileCapabilities, FolderCapabilities, WorkspaceObjectCapabilities } from '../../types/workspace.access';
import type { WorkspaceObjectItem } from '../../types/workspace.table';

export * from './workspace.file-upload';
export * from './workspace.global-search';
export * from './workspace.brand';

export namespace FetchWorkspaceFiles {
  export type Request = {
    workspaceId: number;
    objectId: number;
  };

  export type Response<T = Date> = {
    breadcrumbs: WorkspaceObject<T>[];
    items: WorkspaceObjectItem<T>[];
    capabilities: WorkspaceObjectCapabilities;
  };
}

export namespace FetchWorkspaceFile {
  export type Request = IWorkspaceFileId;

  export type Response<T = Date> = {
    breadcrumbs: WorkspaceObject<T>[];
    capabilities: FileCapabilities;
    meta: {
      accessCount: number;
    };
    object: WorkspaceObject<T>;
    file: WorkspaceFile<T>;
    fileVersion: {
      id: number;
      generateTags: boolean;
    };
    tags: GroupTag[];
    preview: WorkspaceFilePreviewData;
    transcript: WorkspaceFileTranscript;
  };
}

export namespace FetchWorkspaceFolder {
  export type Request = IWorkspaceFolderId;

  export type Response<T = Date> = {
    breadcrumbs: WorkspaceObject<T>[];
    object: WorkspaceObject<T>;
    folder: WorkspaceFolder<T>;
  };
}

export namespace GetFileSummary {
  export type Request = IWorkspaceFileId;

  export type Response = {
    summary: WorkspaceFileSummaryData;
  };
}

export namespace RegenerateFileSummary {
  export type Request = IWorkspaceFileId;

  export type Response = {
    summary: WorkspaceFileSummaryData;
  };
}

export namespace GenerateTags {
  export type Request = {
    fileIds: number[];
  };

  export type Response = {
    queued: number;
  };
}

export namespace DownloadFileSummary {
  export type Request = IWorkspaceFileId;
}

export namespace DownloadFile {
  export type Request = IWorkspaceFileId & {
    workspaceId: number;
  };
  export type Response = {
    file: {
      name: string;
    };
    url: string;
  };
}

export namespace DeleteFile {
  export type Request = IWorkspaceFileId & {
    workspaceId: number;
  };
}


export namespace RenameFolder {
  export const paramsSchema = z.object({
    workspaceId: z.coerce.number(),
    folderId: z.coerce.number(),
  });

  export const bodySchema = z.object({
    name: z.string().min(1, 'Folder name is required'),
  });

  export type Params = z.infer<typeof paramsSchema>;
  export type Body = z.infer<typeof bodySchema>;
}

export namespace RenameFile {
  export const paramsSchema = z.object({
    workspaceId: z.coerce.number(),
    fileId: z.coerce.number(),
  });

  export const bodySchema = z.object({
    name: z.string().min(1, 'File name is required'),
  });

  export type Params = Required<z.infer<typeof paramsSchema>>;
  export type Body = Required<z.infer<typeof bodySchema>>;
}

export namespace GetFolder {
  export type Request = IWorkspaceFolderId;

  export type Response = {
    breadcrumbs: WorkspaceObject[];
    capabilities: FolderCapabilities;
    features: {
      insightsChat: boolean;
      // transcriptPublishType: TranscriptPublishType | null;
    };
    folder: WorkspaceFolder;
    meta: {
      accessCount: number;
      projectId: number;
    };
    object: WorkspaceObject;
  };
}

export namespace CreateFolder {
  export const paramsSchema = z.object({
    workspaceId: z.coerce.number(),
  });

  export const bodySchema = z.object({
    name: z.string().min(1, 'Folder name is required'),
    parentObjectId: z.coerce.number(),
  });

  export const responseSchema = z.void();

  export type Body = Required<z.infer<typeof bodySchema>>;
  export type Params = Required<z.infer<typeof paramsSchema>>;
  export type Response = Required<z.infer<typeof responseSchema>>;
}

export namespace DeleteFolder {
  export const paramsSchema = z.object({
    workspaceId: z.coerce.number(),
    folderId: z.coerce.number(),
  });

  export type Params = z.infer<typeof paramsSchema>;
}

